import { MagnifyingGlass, X } from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import Input from "@/core/components/dv-forms/input";
import { useForm } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import { BinanceService as ExternalBinanceService } from "@/core/services/external/binanceService";
import Loading from "@/core/components/globals/loading";
import { get } from "lodash";
import { CoinModelRequest, DcaStrategy } from "@/types/dca";

interface IForm {
  search: string;
  coins: CoinModelRequest[];
}

const ListCoins = ({ 
  onClose, 
  dcaModel, 
  isCreatingDca, 
  handleAddCoins 
}: { 
  onClose: () => void, 
  dcaModel: DcaStrategy, 
  isCreatingDca: boolean, 
  handleAddCoins: (data: CoinModelRequest[]) => void }) => {
  const [coins, setCoins] = useState<string[]>([]);
  const { register, watch, handleSubmit, setValue, getValues, formState: { errors }, trigger } = useForm({
    defaultValues: {
      search: '',
      coins: []
    } as IForm,
    mode: 'onChange',
  });

  const { mutate, isLoading } = useMutation({
    mutationFn: () => ExternalBinanceService.getAllCoins(),
    onSuccess: (data) => {
      setCoins(data);
      setValue('coins', data.map((coin: string) => ({
        name: coin,
        percentage: get(dcaModel, 'coinModelRequest', []).find((selectedCoin: CoinModelRequest) => selectedCoin.name === coin)?.percentage || 0
      })));
    },
  });

  const onSubmit = (data: IForm) => {
    // TODO: Add the coins
    handleAddCoins(data.coins.filter((coin: CoinModelRequest) => coin.percentage > 0));
  }

  const checkPercentage = () => {
    const totalPercentage = getValues('coins').reduce((acc: number, coin: CoinModelRequest) => acc + (coin.percentage || 0), 0);
    if (totalPercentage > 100) {
      return 'The sum of the percentages must be 100';
    }
    if (totalPercentage === 0 || totalPercentage < 100) {
      return false;
    }
    return true;
  }

  useEffect(() => {
    const subscription = watch(() => {
      if (errors.coins && Array.isArray(errors.coins)) {
        errors.coins.forEach((error: any, index: number) => {
          if (error.percentage.message) {
            // Only trigger if trigger is defined
            trigger && trigger(`coins.${index}.percentage`);
          }
        });
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, errors, trigger]);

  useEffect(() => {
    // TODO: Get the coins
    mutate();
  }, []);

  return (
    <div className="general-modal" style={{ maxWidth: '528px' }}>
      <div className="general-modal__header">
        Coins
        <div className="general-modal__close" onClick={onClose}>
          <X />
        </div>
      </div>
      <div className="general-modal__content dca__add-coins" style={{ overflow: 'unset' }}>
        <Input startAdornment={<MagnifyingGlass size={24} />} name="search" register={register} />
        <div className="dca__add-coins--table">
          <div className="dca__add-coins--table--header"  >
            <div>#</div>
            <div>Coin</div>
            <div>Percentage</div>
          </div>
          {
            isLoading ? <Loading /> : (
              <div className="dca__add-coins--table--body overflow-auto">
                {
                  coins
                    .map((coin, index) => (
                      <div 
                        style={{ 
                          display: `${coin.toLowerCase().toLowerCase().includes(watch('search').toLocaleLowerCase()) ? 'grid' : 'none'}` 
                        }}
                        className="dca__add-coins--table--body--item" 
                        key={coin}
                      >
                        <div className="paragraph-sm">{index + 1}</div>
                        <div className="paragraph-sm">{coin}</div>
                        <div>
                          <Input 
                            type="number" 
                            name={`coins[${index}].percentage`} 
                            register={register} 
                            suffix="%" 
                            errors={errors}
                            rules={{ validate: () => checkPercentage(), valueAsNumber: true }}
                            onBlur={() => setValue(`coins.${index}.percentage`, getValues(`coins.${index}.percentage`) || 0)}
                          />
                        </div>
                      </div>
                    ))
                }
              </div>
            )
          }
        </div>
      </div>
      <div className="general-modal__footer">
        <button 
          disabled={checkPercentage() !== true || isCreatingDca} 
          className="cta-button cta-large cta-blue" 
          onClick={handleSubmit(onSubmit)}
        >
          {isCreatingDca ? 'Creating...' : 'Confirm'}
        </button>
      </div>
    </div>
  )
}

export default ListCoins;